// The station's home: how each line is going here, then the pod to work next
// on each line. The day's score leads because it is short and everyone who
// opens the screen reads it; the pod list sits straight under it.
//
// What is NOT here any more is "Needs you". Its cards sat above the pod list
// and, on a phone, pushed the next pod off the bottom of the screen for a list
// the operator mostly could not act on — an andon is answered in the Andon
// app, a quality alert by its team. The Alerts tab still carries every one of
// them, and its badge on the tab bar is the count; the home screen keeps only
// what changes what the operator does next.
//
// One layout on every screen. A phone folds the day's score into the top of
// the flowline card; a tablet carries it in the header and starts straight on
// the flowline rows. A leader who has drilled into a station sees exactly
// this, with a way back to the floor at the top.
//
// WITH THE ANDON PREVIEW ON the station's open andons AND quality alerts come
// back, as frame v2.12 draws them (template L129-148, script L862): "Open at
// this work centre · N" sits under the next pod, one row each — a dot, the
// title, "AN00114 · Ben on it · ETA 5 min" or "Nobody yet · 4 min over", and
// Reply — with "Andon ›" to the tab. Under that, the day's "Sorted by Dean ·
// 10:31" lines (the spec's amendment). Both sections are absent when there is
// nothing to say.

import { useMemo } from 'react';
import type { AndonAlertVM } from '@/api/andon/types';
import { Banner } from '@/components/ui/Banner';
import { InstallBanner } from '@/components/ui/InstallBanner';
import { Icon } from '@/components/ui/Icon';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { FlowlineRow, ScoreRow, StatChip, lineTone } from '@/components/home/Score';
import { type NeedCardVM } from '@/components/home/NeedCard';
import { PodStateChip } from '@/components/pod/PodParts';
import { paceWord } from '@/components/shell/AppHeader';
import { useTick } from '@/hooks/useTick';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import {
  useFlowlineTopology,
  useMyAlerts,
  usePrefetchJob,
  useQueue,
  useScopeFlowlines,
  useScopeFlowlineIds,
  useScore,
  useStationAlerts,
  useStationBlocked,
} from '@/queries';
import { useAndonFloor } from '@/queries/andon';
import { andonNeedCard, qualityNeedCard } from '@/queries/leader';
import { homeAndonRows, overBy } from '@/lib/homeAndons';
import { fmtLocalTime, paceLabel } from '@/lib/pace';
import { outboundChips, outboundFor } from '@/lib/outbound';
import { STAR_COLOR } from '@/lib/severity';
import { stationTone, type StationWord } from '@/lib/stationTone';
import { clockTime } from '@/lib/thread';
import { useStore } from '@/store';
import type { AlertVM, AndonVM, NeedsYouVM, PodVM } from '@/api/types';

export interface NeedActions {
  goToStation: (workcentreId: number, name: string) => void;
  openAndon: (andonId: number) => void;
  openQuality: (alertId: number) => void;
  openTodo: (item: NeedsYouVM) => void;
  /**
   * "Take it" from a card, straight into the ETA sheet (frame v2.12 script
   * L853: `takeAndon`). Only a screen that hosts a `TakeSheet` sets it, and
   * only for a session that may take; a card without it opens the thread.
   */
  takeAndon?: (andon: AndonVM) => void;
}

export function HomeScreen({
  tablet,
  drilled,
  onBackToFloor,
  onOpenPod,
  onOpenPods,
  onOpenAndons,
}: {
  tablet: boolean;
  /** A leader looking at a station: show the way back to the floor. */
  drilled: boolean;
  onBackToFloor: () => void;
  /** Opens the pod for review. Starting it is a second, deliberate tap. */
  onOpenPod: (pod: PodVM) => void;
  /** The Pods tab, landed on one line's lane — or wherever it lands, for null. */
  onOpenPods: (flowlineId: number | null) => void;
  /** The Andon tab: the "Andon ›" link on the open rows. */
  onOpenAndons: () => void;
}) {
  const t = useT();
  const wcName = useStore((s) => s.workcentreName);
  const workcentreId = useStore((s) => s.workcentreId);
  const flowlineId = useStore((s) => s.flowlineId);
  const startedWoId = useStore((s) => s.startedWoId);
  const setStation = useStore((s) => s.setStation);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const block = useStationBlocked();

  const queue = useQueue();
  const pods = queue.data ?? [];
  const first = pods[0];
  usePrefetchJob(first?.woId ?? null, first?.moId ?? null);

  const { data: score, isPending: scorePending, states } = useScore();
  const scopeOptions = useScopeFlowlines();
  const scopeIds = useScopeFlowlineIds();
  const topology = useFlowlineTopology(scopeIds);

  // The cards stay in flowline order, 1..n, so a card is always where it was
  // last time — a list that reshuffles between polls is a list an operator
  // stops trusting. Nothing on them ranks one line over another any more:
  // not a Priority flag, not a starvation banner, not a badge naming the
  // station downstream as starved. Each card says only how many pods are
  // waiting between here and the next cell (lib/outbound.ts) and leaves the
  // reading of that to the person holding the phone.
  const lines = score?.lines ?? [];

  /** Scope is the flowline filter over the station, never a station change. */
  const pickScope = (id: number | null) => {
    const wc = workcentreId != null && wcName ? { id: workcentreId, name: wcName } : null;
    const chosen = id == null ? null : scopeOptions.find((o) => o.id === id);
    setStation(wc, chosen ? { id: chosen.id, name: chosen.name } : null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 className="sr-only">{wcName ?? t.station}</h1>

      {drilled && (
        <button
          type="button"
          className="press"
          onClick={onBackToFloor}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            minHeight: 36,
            padding: '0 12px',
            borderRadius: 10,
            background: 'var(--surface2)',
            color: 'var(--sub)',
            font: '600 11.5px Manrope,sans-serif',
            alignSelf: 'flex-start',
          }}
        >
          <Icon name="back" size={13} />
          {t.backToOverviewT}
          <span style={{ color: 'var(--ink)' }}>· {wcName}</span>
        </button>
      )}

      {/* How each line is going here comes first: it is the one thing on the
          screen every person who opens it reads, operator or leader, and it
          is short — the pod to work next sits directly under it. */}
      {(score && lines.length > 0) || (!tablet && scorePending && !score) ? (
        <section aria-label={t.todayByFlowlineT}>
          <SectionHead label={`${t.todayByFlowlineT} · ${wcName ?? ''}`} />
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 14,
              padding: '6px 14px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!tablet &&
              (score ? (
                <ScoreRow
                  done={score.done}
                  target={score.target}
                  sentence={paceSentence(score.dueNow, score.target, score.done, t)}
                  chip={paceWord(score, t)}
                  chipTone={lineTone(score)}
                />
              ) : (
                <Skeleton height={46} radius={8} />
              ))}
            {lines.map((l) => {
              const st = states.find((s) => s.workcentreId === workcentreId && s.flowlineId === l.flowlineId);
              const tone = st ? stationTone(st, l.done, l.target, l.dueNow) : null;
              const chipTone = lineTone(l);
              return (
                <FlowlineRow
                  key={l.flowlineId}
                  short={l.short}
                  done={l.done}
                  target={l.target}
                  dueNow={l.dueNow}
                  delta={l.delta}
                  chip={l.delta == null ? '—' : paceLabel(l.delta).replace('-', '−')}
                  chipTone={chipTone}
                  exText={tone && tone.word !== 'onPace' ? toneWord(tone.word, tone.behind, t) : ''}
                  exTone={tone ? (tone.kind === 'grey' ? 'grey' : tone.kind) : 'neutral'}
                  onClick={() => pickScope(flowlineId === l.flowlineId ? null : l.flowlineId)}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      <InstallBanner />

      {block.blocked && (
        <Banner tone="danger">
          <strong>{t.wcBlocked}</strong>
          {block.reason ? ` — ${block.reason}` : ''}
        </Banner>
      )}

      <section aria-label={t.nextPodByFlowlineT}>
        <SectionHead
          label={`${t.nextPodByFlowlineT} · ${wcName ?? ''}`}
          link={{ label: `${t.allTodaysPodsT} ›`, onClick: () => onOpenPods(null) }}
        />
        {queue.isPending || (scorePending && !score) ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <Skeleton height={64} radius={13} />
            <Skeleton height={64} radius={13} />
          </div>
        ) : queue.isError ? (
          <Banner tone="danger" action={{ label: t.retry, onClick: () => queue.refetch() }}>
            {(queue.error as Error).message}
          </Banner>
        ) : lines.length === 0 ? (
          <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
            <EmptyState>
              <div style={{ font: "700 15px 'Space Grotesk',sans-serif", color: 'var(--ink)', marginBottom: 6 }}>
                {t.nothingQueuedHereT}
              </div>
              {pods.length === 0 && scopeOptions.length > 1 && flowlineId != null ? t.nothingWaitingLineT : t.nothingWaitingStationT}
            </EmptyState>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lines.map((line) => {
              const pod = pods.find((p) => p.flowlineId === line.flowlineId);
              const outbound =
                workcentreId == null ? null : outboundFor(topology, workcentreId, line.flowlineId, line.outboundWaiting);
              const chips = outbound ? outboundChips(outbound, t) : [];
              return (
                <div
                  key={line.flowlineId}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 13,
                    padding: '11px 13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ width: 34, flex: 'none', font: "700 13px 'Space Grotesk',sans-serif" }}>{line.short}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        font: "700 13.5px 'Space Grotesk',sans-serif",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pod ? pod.mo : t.nothingQueuedHereT}
                      {pod && (
                        <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
                          {' · '}
                          {[pod.proj, pod.type, ...pod.spec.slice(0, 1)].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    {(pod || chips.length > 0) && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {/* The pod's own state first: the queue holds pods
                            still upstream as well as the ones standing here,
                            and "next" must not be read as "here". */}
                        {pod && <PodStateChip state={pod.state} started={pod.woId === startedWoId} />}
                        {/* The count is the way into this line's day in Pods —
                            the card's Open button takes the one pod, the count
                            takes the whole line. */}
                        {chips.map((c, i) => (
                          <button
                            key={i}
                            type="button"
                            className="press"
                            onClick={() => onOpenPods(line.flowlineId)}
                            aria-label={`${line.short} · ${c.label} · ${t.allTodaysPodsT}`}
                            style={{ display: 'flex', minHeight: 28, alignItems: 'center' }}
                          >
                            <StatChip tone={c.tone} size="sm">
                              {c.label} ›
                            </StatChip>
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="press"
                    disabled={!pod}
                    onClick={() => pod && onOpenPod(pod)}
                    style={{
                      flex: 'none',
                      minHeight: 44,
                      borderRadius: 11,
                      padding: '0 15px',
                      font: '700 12px Manrope,sans-serif',
                      background: 'transparent',
                      color: pod ? 'var(--ink)' : 'var(--sub)',
                      border: '1px solid var(--line)',
                    }}
                  >
                    {t.openT}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* What is open here, with the preview on — after the next pod (the
          thing the operator came for). Quiet rows, and the section is absent
          when there is nothing to say: this is the one screen that must not
          push the next pod off the bottom. */}
      {andonPreview && <OpenHere onOpenAndons={onOpenAndons} />}

      {andonPreview && <SortedHere />}
    </div>
  );
}

/**
 * "Open at this work centre · N" — the station's open andons and the quality
 * alerts raised here or by this operator, from the module's floor read and
 * the shop floor's own alert lists (frame script L862 `openHere`).
 */
function OpenHere({ onOpenAndons }: { onOpenAndons: () => void }) {
  const t = useT();
  const now = useTick();
  const wcName = useStore((s) => s.workcentreName);
  const operatorId = useStore((s) => s.operator?.id ?? null);
  const openAndon = useStore((s) => s.openAndon);
  const openQuality = useStore((s) => s.openQualityAlert);
  const floor = useAndonFloor('all', true);
  const mine = useMyAlerts();
  const here = useStationAlerts();
  const andons = useMemo(() => homeAndonRows(floor.data?.alerts ?? [], wcName, now).open, [floor.data, wcName, now]);
  // Raised by me or stamped with this station, once each.
  const alerts = useMemo(() => {
    const seen = new Set<number>();
    return [...(mine.data ?? []), ...(here.data ?? [])].filter((q) => (seen.has(q.id) ? false : (seen.add(q.id), true)));
  }, [mine.data, here.data]);
  if (andons.length + alerts.length === 0) return null;
  return (
    <section aria-label={t.homeOpenAtWorkCentreT}>
      <SectionHead label={`${t.homeOpenAtWorkCentreT} · ${andons.length + alerts.length}`} link={{ label: `${t.andonTabT} ›`, onClick: onOpenAndons }} />
      <OpenHereList andons={andons} alerts={alerts} now={now} operatorId={operatorId} onOpenAndon={openAndon} onOpenQuality={openQuality} />
    </section>
  );
}

/**
 * The rows themselves, as frame v2.12 template L136-145 draws them: a dot,
 * the title, the meta line, and Reply. Takes its lists as props so the
 * kitchen sink can draw it from fixtures.
 *
 * The meta line is the two things the person who raised it is waiting to
 * hear — who has it and when they said they would be there — or how long
 * nobody has: "AN00114 · Ben on it · ETA 5 min", "AN00114 · Nobody yet · 4
 * min over". The frame also counts the replies; the floor read carries no
 * message count, so that part is not drawn.
 */
export function OpenHereList({
  andons,
  alerts,
  now,
  operatorId,
  onOpenAndon,
  onOpenQuality,
}: {
  andons: AndonAlertVM[];
  alerts: AlertVM[];
  now: number;
  operatorId: number | null;
  onOpenAndon: (id: number) => void;
  onOpenQuality: (id: number) => void;
}) {
  const t = useT();
  const rows = [
    ...andons.map((a) => {
      const over = overBy(a, now);
      const unowned = a.owner.unowned;
      return {
        key: `an:${a.id}`,
        dot: unowned ? 'var(--danger)' : STAR_COLOR[a.stars],
        border: unowned && a.stars === 3 ? 'var(--danger)' : 'var(--line)',
        title: a.title,
        meta: [
          a.qa,
          unowned
            ? `${t.nobodyYetT}${over != null ? ` · ${over} ${t.minOverT}` : ''}`
            : `${a.owner.name?.split(' ')[0] ?? ''} ${t.onItT}${a.etaMinutes != null ? ` · ETA ${a.etaMinutes} ${t.minT}` : ''}`,
        ].join(' · '),
        metaColor: unowned ? 'var(--danger)' : 'var(--sub)',
        onOpen: () => onOpenAndon(a.id),
      };
    }),
    ...alerts.map((q) => ({
      key: `qa:${q.id}`,
      dot: 'var(--accent)',
      border: 'var(--line)',
      title: `${q.qa} · ${q.title}`,
      meta: [t.qualityT, q.stage, q.employeeId != null && q.employeeId === operatorId ? `${t.homeRaisedByYouT} ${fmtLocalTime(q.createdAt)}` : q.team].join(' · '),
      metaColor: 'var(--sub)',
      onOpen: () => onOpenQuality(q.id),
    })),
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ background: 'var(--surface)', border: `1px solid ${r.border}`, borderRadius: 13, padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: r.dot, flex: 'none' }} />
          <button type="button" className="press-row" onClick={r.onOpen} style={{ flex: 1, minWidth: 0, textAlign: 'left', whiteSpace: 'normal' }}>
            <span style={{ display: 'block', font: '700 12.5px Manrope,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
            <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: r.metaColor, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.meta}</span>
          </button>
          <button type="button" className="press" onClick={r.onOpen} style={{ flex: 'none', minHeight: 40, borderRadius: 10, padding: '0 12px', border: '1px solid var(--line)', font: '700 11.5px Manrope,sans-serif', color: 'var(--ink)' }}>
            {t.replyT}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * "Sorted by Dean · 10:31" — one line each for the day's closes at this
 * station, so an operator back from a break can see the andon they raised
 * was answered rather than wondering whether it vanished. The spec's
 * amendment to the frame; last on the screen, because it is the record
 * and not the work.
 */
function SortedHere() {
  const t = useT();
  const now = useTick();
  const wcName = useStore((s) => s.workcentreName);
  const openAndon = useStore((s) => s.openAndon);
  const floor = useAndonFloor('all', true);
  const list = useMemo(() => homeAndonRows(floor.data?.alerts ?? [], wcName, now).sorted, [floor.data, wcName, now]);
  if (list.length === 0) return null;

  return (
    <section aria-label={t.homeSortedHereTodayT}>
      <SectionHead label={t.homeSortedHereTodayT} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((a) => (
          <button
            key={a.id}
            type="button"
            className="press-row"
            onClick={() => openAndon(a.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 36, padding: '4px 12px', borderRadius: 12, textAlign: 'left' }}
          >
            <Icon name="checkCircle" size={14} strokeWidth={2.2} style={{ color: 'var(--pos)', flex: 'none' }} />
            <span style={{ flex: 1, minWidth: 0, font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {t.homeSortedByT} {a.closedBy?.split(' ')[0] ?? '—'} · {a.closedAt != null ? clockTime(a.closedAt) : ''}
              </span>
              {' · '}
              {a.qa} {a.title}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** The mock's "needs you" cards for an operator: their andons, their alerts, their to-dos. */
export function useOperatorNeedCards(
  items: NeedsYouVM[],
  actions: NeedActions,
  isLeader: boolean,
  workcentreId: number | null,
): NeedCardVM[] {
  const t = useT();
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const operatorId = useStore((s) => s.operator?.id ?? null);
  return useMemo(
    () =>
      items.map((item) => {
        if (item.kind === 'andon' && item.andon) {
          const a = item.andon;
          return andonNeedCard(a, t, andonAppUrl, a.flowlineName.replace(/^Flowline\s*/i, 'FL '), actions, isLeader, workcentreId, andonPreview);
        }
        if (item.kind === 'quality' && item.alert) {
          const q = item.alert;
          return qualityNeedCard(q, t, isLeader || q.employeeId === operatorId, actions.openQuality);
        }
        return {
          key: item.key,
          tag: 'TO-DO',
          tagTone: 'neutral',
          urgent: false,
          title: item.title,
          meta: item.meta,
          onOpen: () => actions.openTodo(item),
        } satisfies NeedCardVM;
      }),
    [items, t, andonAppUrl, andonPreview, actions, isLeader, workcentreId, operatorId],
  );
}

/**
 * "Pace says 12 by 14:10 · 7 to go" — or why there is nothing to say.
 *
 * Nothing at all once the shift is over: with all of the day's time spent the
 * pace line has reached the target, so the sentence could only ever repeat the
 * figure beside it ("Pace says 42 by 21:06") against a clock that has stopped
 * meaning anything. The score and its chip already say how the day ended.
 */
export function paceSentence(dueNow: number | null, target: number | null, done: number, t: Translations): string {
  if (target == null) return t.noTargetTodayT;
  if (dueNow == null) return t.notWorkingTodayT;
  if (dueNow >= target) return '';
  const now = fmtLocalTime(new Date().toISOString().slice(0, 19).replace('T', ' '));
  return `${t.paceSaysT} ${dueNow} ${t.byT} ${now} · ${Math.max(0, target - done)} ${t.toGoT}`;
}

/** The word a station wears on a line, in the operator's language. */
export function toneWord(word: StationWord, behind: number, t: Translations): string {
  switch (word) {
    case 'stopped':
      return t.stoppedT;
    case 'offShift':
      return t.offShiftT;
    case 'noTarget':
      return t.noTargetT;
    case 'noWork':
      return t.noWorkT;
    case 'queueEmpty':
      return t.queueEmptyWordT;
    case 'done':
      return t.done;
    case 'starved':
      return t.starvedWordT;
    case 'behind':
      return `${t.behindWordT} ${behind}`;
    case 'onPace':
      return t.onPaceT;
  }
}

export function SectionHead({
  label,
  sub,
  link,
}: {
  label: string;
  sub?: string;
  link?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, minWidth: 0 }}>
      {/* Truncates rather than overflows: a label wider than the screen was
          the one thing on the page that could make it scroll sideways. */}
      <span
        className="sectionLabel"
        style={{ marginBottom: 0, whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flex: '0 1 auto' }}
      >
        {label}
      </span>
      {sub && (
        <span
          style={{
            font: '500 11px Manrope,sans-serif',
            color: 'var(--sub)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sub}
        </span>
      )}
      {link && (
        <button
          type="button"
          className="press"
          onClick={link.onClick}
          style={{ marginLeft: 'auto', font: '700 11.5px Manrope,sans-serif', color: 'var(--accent)', minHeight: 24, whiteSpace: 'nowrap' }}
        >
          {link.label}
        </button>
      )}
    </div>
  );
}
